import type { ControllerProps } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { FieldValueConverter } from './dynamic-form.interfaces';

export interface OdinControllerProps<T> extends ControllerProps {
  onChange?: (value: T) => void;
  fieldValueConverter?: FieldValueConverter;
}

/**
 * This is used to override hook forms onChange method. To make dynamic form less "hacky" this component will be used to
 * combine dynamic form's onChange method with hook forms and fire them together. Without this component each hook form
 * wrapper implementation would have to perform this task itself which would cause an issue if someone forgot to do it.
 * @param {Control<FieldValues> | undefined} control - Hook forms control object.
 * @param {string} name - The name given to this field in hook form.
 * @param defaultValue - The default value assigned to the control.
 * @param {({field, fieldState, formState}: {field: ControllerRenderProps<FieldValues, FieldPath<FieldValues>>, fieldState: ControllerFieldState, formState: UseFormStateReturn<FieldValues>}) => React.ReactElement} render - The render method used to create the control used by hook form.
 * @param {(<T>(value: T) => void) | undefined} onChange - Dynamic form's onChange method to combine with hook form's.
 * @param {FieldValueConverter} fieldValueConverter - Converts a fields raw value into an acceptable dynamic form value.
 * @returns {JSX.Element}
 * @constructor
 */
const OdinController = <T,>({
  control,
  name,
  defaultValue,
  render,
  onChange,
  fieldValueConverter,
}: OdinControllerProps<T>): JSX.Element => {
  // Can not apply undefined to defaultValue (https://legacy.react-hook-form.com/api/usecontroller/controller)
  const _defaultValue = defaultValue === undefined ? null : defaultValue;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={_defaultValue}
      render={({ field, fieldState, formState }) => {
        const { onChange: hfOnChange, ...rest } = field;

        const finalOnChange = (value: T) => {
          // dynamic form's onChange function already uses the field value converter
          onChange?.(value);

          // Calling onChange with undefined is not valid (https://legacy.react-hook-form.com/api/usecontroller/controller)
          const _value = value === undefined ? null : value;
          hfOnChange(fieldValueConverter ? fieldValueConverter(_value) : _value);
        };

        const newField = {
          ...rest,
          onChange: finalOnChange,
        };

        return render({ field: newField, fieldState, formState });
      }}
    />
  );
};

export default OdinController;
